import { HttpClient } from '@angular/common/http';
import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import { ApiEnvironmentService } from './api-environment/api-environment.service';
import { IApiEnvironmentModel } from './api-environment/api-environment.model';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AppInitializerService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiEnvironmentService = inject(ApiEnvironmentService);
  private readonly platformId = inject(PLATFORM_ID);

  public async loadApiEnvironment(): Promise<boolean> {
    // During SSR, use environment variables directly to avoid HTTP request issues
    if (!isPlatformBrowser(this.platformId)) {
      // Ensure HTTPS for production (fix mixed content errors)
      const ensureHttps = (url: string) => {
        const normalized = url?.startsWith('http://') ? url.replace('http://', 'https://') : url;
        return normalized?.replace(/\/$/, '');
      };

      const baseAdminUrl = ensureHttps(process.env['apiBase'] || environment.apiEnv.baseAdminUrl);
      const baseAccountUrl = ensureHttps(process.env['apiAccount'] || environment.apiEnv.baseAccountUrl);
      const searchStaxUrl = ensureHttps(process.env['searchStaxUrl'] || environment.apiEnv.searchStaxUrl);
      const searchStaxToken = process.env['searchStaxToken'] || environment.apiEnv.searchStaxToken;
      const searchStaxAnalyticsToken = process.env['searchStaxAnalyticsToken'] || environment.apiEnv.searchStaxAnalyticsToken;

      const ssrConfig: IApiEnvironmentModel = {
        baseAdminUrl,
        baseAccountUrl,
        baseSubrequestUrl: `${baseAdminUrl}/subrequests?_format=json`,
        searchStaxUrl: searchStaxUrl,
        searchStaxToken: searchStaxToken,
        searchStaxAnalyticsToken: searchStaxAnalyticsToken
      };

      this.apiEnvironmentService.setApiEnvironment(ssrConfig);
      return true;
    }

    // In browser, fetch from /apiEnv endpoint to get runtime config
    try {
      const response = await firstValueFrom(
        this.httpClient.get<IApiEnvironmentModel>('/apiEnv')
      );

      // The key lives in Acquia and comes from /apiEnv; only the local environment file defines a fallback
      const fallbackEnvironment: IApiEnvironmentModel = environment.apiEnv;
      this.apiEnvironmentService.setApiEnvironment({
        ...response,
        googleMapsApiKey: response.googleMapsApiKey || fallbackEnvironment.googleMapsApiKey
      });
      return true;
    } catch {
      console.error('Failed to load API environment from /apiEnv, using fallback config');
      this.apiEnvironmentService.setApiEnvironment(environment.apiEnv);
      return false;
    }
  }
}
